import React, { useState, useRef } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Search, Filter, ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQSection {
  title: string;
  questions: {
    question: string;
    answer: string | string[];
  }[];
}

const faqData: FAQSection[] = [
  {
    title: "Général",
    questions: [
      {
        question: "Qu'est-ce que le Diagnostic Énergétique ?",
        answer: "Le diagnostic énergétique est un outil permettant d'évaluer votre état énergétique actuel en identifiant les centres énergétiques alignés ou désalignés dans votre corps. Basé sur le Human Design, il offre une compréhension profonde de votre énergie et des moyens de l'harmoniser."
      },
      {
        question: "À qui s'adresse cette application ?",
        answer: "Cette application est conçue pour toute personne souhaitant mieux comprendre son énergie, améliorer son bien-être ou travailler sur des aspects spécifiques de sa vie (relations, travail, équilibre personnel)."
      },
      {
        question: "Comment fonctionne l'évaluation énergétique ?",
        answer: "L'évaluation repose sur une série de questions simples. Vos réponses permettent de déterminer quels centres énergétiques sont affectés ou désalignés et d'obtenir des recommandations personnalisées."
      }
    ]
  },
  {
    title: "Profil et Connexion",
    questions: [
      {
        question: "Comment créer un compte ?",
        answer: "Cliquez sur \"S'inscrire\" et entrez votre email, nom et mot de passe. Un email de confirmation vous sera envoyé pour vérifier votre compte."
      },
      {
        question: "Je n'arrive pas à me connecter. Que faire ?",
        answer: "Assurez-vous que vos identifiants sont corrects. Si le problème persiste, cliquez sur \"Mot de passe oublié\" pour réinitialiser votre mot de passe."
      },
      {
        question: "Comment modifier mes informations personnelles ?",
        answer: "Accédez à votre Profil dans le menu burger. Vous pouvez mettre à jour votre nom, email ou d'autres informations."
      },
      {
        question: "Mes données personnelles sont-elles sécurisées ?",
        answer: "Oui, toutes vos données sont protégées grâce à un cryptage avancé et stockées de manière sécurisée. Nous respectons les normes RGPD (Règlement Général sur la Protection des Données)."
      }
    ]
  },
  {
    title: "Centres Énergétiques",
    questions: [
      {
        question: "Quels sont les principaux centres énergétiques ?",
        answer: [
          "Les centres incluent :",
          "• Ajna : Intuition, clarté mentale",
          "• Cœur : Volonté, amour-propre",
          "• Sacral : Énergie vitale, action",
          "• Couronne : Inspiration, connexion spirituelle",
          "• Racine : Stabilité, motivation",
          "• Gorge : Communication, expression",
          "• Émotionnel : Gestion des émotions",
          "• Rate : Intuition instinctive, bien-être"
        ]
      },
      {
        question: "Comment savoir quel centre est affecté ?",
        answer: "Vos réponses aux questions du diagnostic permettent d'identifier les centres affectés. Les résultats indiquent clairement quels centres nécessitent une attention."
      }
    ]
  },
  {
    title: "Problèmes techniques",
    questions: [
      {
        question: "Je rencontre une erreur dans l'application. Que faire ?",
        answer: "Redémarrez l'application et vérifiez votre connexion internet. Si le problème persiste, contactez le support technique avec une description détaillée de l'erreur."
      },
      {
        question: "Pourquoi certaines données ne s'affichent pas correctement ?",
        answer: "Cela peut être dû à un problème temporaire de synchronisation. Essayez de rafraîchir la page ou de vider le cache de votre navigateur."
      },
      {
        question: "Comment signaler un bug ou une erreur ?",
        answer: "Utilisez la section \"Support\" dans le menu pour signaler tout problème technique."
      }
    ]
  }
];

export default function FAQ() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const topRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const filteredFAQ = faqData.map(section => ({
    ...section,
    questions: section.questions.filter(
      q => q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (typeof q.answer === 'string' && q.answer.toLowerCase().includes(searchTerm.toLowerCase())) ||
           (Array.isArray(q.answer) && q.answer.some(a => a.toLowerCase().includes(searchTerm.toLowerCase())))
    )
  })).filter(section => section.questions.length > 0);

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader>
            <h1 className="text-2xl font-bold">Foire Aux Questions</h1>
            <p className="text-gray-600">
              Trouvez rapidement des réponses à vos questions
            </p>
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher une question..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border rounded-md px-3 py-2"
              >
                <option value="all">Toutes les catégories</option>
                {faqData.map((section, index) => (
                  <option key={index} value={section.title.toLowerCase()}>
                    {section.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Navigation rapide */}
            <div className="flex flex-wrap gap-2 mt-4">
              {faqData.map((section, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    document.getElementById(section.title)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {section.title}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              {filteredFAQ.map((section, index) => (
                <div key={index} className="space-y-4">
                  <h2 className="text-xl font-semibold text-[#54075B]">
                    {section.title}
                  </h2>
                  <div className="space-y-4">
                    {section.questions.map((item, qIndex) => (
                      <div key={qIndex} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                        <h3 className="font-medium text-gray-900 mb-2">
                          {item.question}
                        </h3>
                        <div className="text-gray-600">
                          {Array.isArray(item.answer) ? (
                            <div className="space-y-1">
                              {item.answer.map((line, lineIndex) => (
                                <p key={lineIndex}>{line}</p>
                              ))}
                            </div>
                          ) : (
                            <p>{item.answer}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {filteredFAQ.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    Aucune question ne correspond à votre recherche.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed bottom-8 right-8 p-3 bg-[#54075B] text-white rounded-full shadow-lg"
            onClick={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })}
          >
            <ArrowUp className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}